import { clamp } from "@common/helpers/collections";
import type {
    BarSpan,
    DensityChartPeriod,
    DensityChartWindow,
} from "./DensityChart.lib";
import type { DensityChartBucket } from "./densityBuckets.lib";
import type { PanDirection } from "./densityViewport.lib";

export type WindowEdge = "start" | "end";

/** The window's body and handles, which keys and drags move. */
export type WindowPart = "move" | WindowEdge;

/** "draw" is a new range drawn from a press outside the window. */
export type WindowDragMode = WindowPart | "draw";

const MIN_BARS = 1;

/** Bar `i` holds days `edges[i]` to `edges[i + 1]`. */
function barEdges(buckets: DensityChartBucket[]): number[] {
    const edges = [0];
    for (const { dates } of buckets) {
        edges.push((edges.at(-1) ?? 0) + dates.length);
    }
    return edges;
}

function trackDays(buckets: DensityChartBucket[]) {
    return {
        dates: buckets.flatMap(({ dates: bucketDays }) => bucketDays),
        edges: barEdges(buckets),
    };
}

export function edgeOf(span: BarSpan, edge: WindowEdge): number {
    return edge === "start" ? span.start : span.end;
}

interface BarDays {
    first: number;
    count: number;
}

function barDays(edges: number[], bar: number): BarDays {
    const first = edges[bar] ?? 0;
    return { first, count: Math.max((edges[bar + 1] ?? first) - first, 1) };
}

/** A day boundary lands inside its bar by its share of that bar's days. */
function dayToBar(day: number, edges: number[]): number {
    const lastBar = Math.max(edges.length - 2, 0);
    const bar = clamp(
        edges.findLastIndex((edge) => edge <= day),
        0,
        lastBar,
    );
    const { first, count } = barDays(edges, bar);
    return bar + (day - first) / count;
}

function barToDay(position: number, edges: number[]): number {
    const lastBar = Math.max(edges.length - 2, 0);
    const bar = clamp(Math.floor(position), 0, lastBar);
    const { first, count } = barDays(edges, bar);
    return first + (position - bar) * count;
}

/**
 * Clamped to the chart's days. A window that doesn't line up with week or
 * month bars covers its days' share of each bar it touches.
 */
export function windowToSpan(
    window: DensityChartWindow,
    buckets: DensityChartBucket[],
): BarSpan {
    const { dates, edges } = trackDays(buckets);
    const lastIndex = dates.length - 1;
    const startIndex = dates.findIndex((date) => date >= window.start);
    const start = startIndex === -1 ? lastIndex : startIndex;
    const end = Math.max(
        dates.findLastIndex((date) => date <= window.end),
        start,
    );
    return { start: dayToBar(start, edges), end: dayToBar(end + 1, edges) };
}

export function spanToPeriod(
    span: BarSpan,
    buckets: DensityChartBucket[],
): DensityChartPeriod {
    const { dates, edges } = trackDays(buckets);
    const start = clamp(
        Math.round(barToDay(span.start, edges)),
        0,
        Math.max(dates.length - 1, 0),
    );
    const end = clamp(
        Math.round(barToDay(span.end, edges)),
        start + 1,
        Math.max(dates.length, start + 1),
    );
    return {
        start: dates[start] ?? "",
        end: dates[end - 1] ?? "",
        count: end - start,
    };
}

/** The bars a window may cover: from `minBar` (the history start, which may sit partway into a bar, else 0) to `barCount`. */
export interface BarLimits {
    barCount: number;
    minBar?: number;
}

interface ResizeSpanParams extends BarLimits {
    span: BarSpan;
    edge: WindowEdge;
    position: number;
}

const latestStart = (span: BarSpan) =>
    Math.max(span.end - MIN_BARS, span.start);
const earliestEnd = (span: BarSpan) =>
    Math.min(span.start + MIN_BARS, span.end);

/**
 * Keeps at least one bar, or holds the edge where it is when the window is
 * already narrower than that, even past `minBar` or `barCount`.
 */
export function resizeSpan({
    span,
    edge,
    position,
    barCount,
    minBar = 0,
}: ResizeSpanParams): BarSpan {
    if (edge === "start") {
        return {
            start: clamp(position, minBar, latestStart(span)),
            end: span.end,
        };
    }
    return {
        start: span.start,
        end: Math.max(Math.min(position, barCount), earliestEnd(span)),
    };
}

/** Whether a resized edge is held against the other edge of `from`, so it can't move `direction`. */
export function isEdgeHeld({
    span,
    from,
    mode,
    direction,
}: {
    span: BarSpan;
    from: BarSpan;
    mode: WindowDragMode;
    direction: PanDirection;
}): boolean {
    if (mode === "start") {
        return direction === "later" && span.start >= latestStart(from);
    }
    return mode === "end" && direction === "earlier"
        ? span.end <= earliestEnd(from)
        : false;
}

interface DragSpanParams extends BarLimits {
    /** A drawn range starts as the bar position pressed, with no width. */
    span: BarSpan;
    mode: WindowDragMode;
    deltaBars: number;
    /** Where the bars in view start and end, for the edges that should stay in view. */
    inView?: { start?: number; end?: number };
}

export function dragSpan({
    span,
    mode,
    deltaBars,
    inView,
    ...limits
}: DragSpanParams): BarSpan {
    const minBar = Math.max(limits.minBar ?? 0, inView?.start ?? -Infinity);
    const barCount = Math.min(limits.barCount, inView?.end ?? Infinity);
    if (mode === "draw") {
        const position = clamp(span.start + deltaBars, minBar, barCount);
        const anchor = clamp(span.start, minBar, barCount);
        return {
            start: Math.min(anchor, position),
            end: Math.max(anchor, position),
        };
    }
    if (mode !== "move") {
        return resizeSpan({
            span,
            edge: mode,
            position: edgeOf(span, mode) + deltaBars,
            barCount,
            minBar,
        });
    }
    const length = span.end - span.start;
    const start = clamp(span.start + deltaBars, minBar, barCount - length);
    return { start, end: start + length };
}

interface SnapSpanParams extends BarLimits {
    span: BarSpan;
    /** The span the drag started from. */
    from: BarSpan;
    mode: WindowDragMode;
}

export const isSameSpan = (a: BarSpan, b: BarSpan): boolean =>
    a.start === b.start && a.end === b.end;

export const shiftSpan = (span: BarSpan, bars: number): BarSpan => ({
    start: span.start + bars,
    end: span.end + bars,
});

/**
 * A moved window rounds both edges, keeping its end on a bar's end even when
 * its start stops partway into a bar; a resize rounds only the dragged edge,
 * so a window narrower than a bar keeps its other edge. A resize whose edge
 * ends where it started (or was held by the minimum width) leaves `from` as
 * it was. A drawn range covers every bar it touches.
 */
export function snapSpan({
    span,
    from,
    mode,
    barCount,
    minBar = 0,
}: SnapSpanParams): BarSpan {
    if (mode === "draw") {
        const start = clamp(
            Math.floor(span.start),
            minBar,
            barCount - MIN_BARS,
        );
        return {
            start,
            end: clamp(Math.ceil(span.end), start + MIN_BARS, barCount),
        };
    }
    if (mode !== "move") {
        const edgePosition = edgeOf(span, mode);
        if (edgePosition === edgeOf(from, mode)) {
            return from;
        }
        return resizeSpan({
            span: from,
            edge: mode,
            position: Math.round(edgePosition),
            barCount,
            minBar,
        });
    }
    if (isSameSpan(span, from)) {
        return from;
    }
    const start = clamp(Math.round(span.start), minBar, barCount - MIN_BARS);
    return {
        start,
        end: Math.max(Math.round(span.end), Math.floor(start) + MIN_BARS),
    };
}
